import { useState, useEffect, useRef } from 'react';

export function useRegionFilters({
  regionId,
  regionArray,
  dateRanges,
  showSelectedRegion,
  defaultRegionBoolean,
  checkForGlobalObject,
}) {
  const [currentDateFiltersForRegion, setCurrentDateFiltersForRegion] = useState([]);
  const [comparePeriodFiltersForRegion, setComparePeriodFiltersForRegion] = useState([]);
  const [compareYtdFiltersForRegion, setCompareYtdFiltersForRegion] = useState([]);
  const [currentYtdFiltersForRegion, setCurrentYtdFiltersForRegion] = useState([]);

  // Use refs to track previous values
  const prevRegionIdRef = useRef();
  const prevDateRangesRef = useRef();

  useEffect(() => {
    // Skip if no region ID is provided
    if (!regionId) return;

    // Skip update if inputs haven't changed
    const dateRangesHash = JSON.stringify(dateRanges);
    if (prevRegionIdRef.current === regionId && prevDateRangesRef.current === dateRangesHash) {
      return;
    }

    // Store current values for next comparison
    prevRegionIdRef.current = regionId;
    prevDateRangesRef.current = dateRangesHash;

    try {
      const regionIdFilter = {
        col: 'region_id',
        op: 'IN',
        val: [regionId],
      };

      // Helper function to create date index filter for region
      const createRegionDateIndexFilter = (range, includeRegionFirst = false) => {
        if (!range || checkForGlobalObject(range)) return null;

        const dateFilters = [
          {
            col: 'dateindex',
            op: '>=',
            val: range.start_year
              ?.toString()
              ?.concat(
                range.start_week <= 9 && range.start_week > 0
                  ? '0'.concat(range.start_week)
                  : range.start_week
              ),
          },
          {
            col: 'dateindex',
            op: '<=',
            val: range.end_year
              ?.toString()
              ?.concat(
                range.end_week <= 9 && range.end_week > 0
                  ? '0'.concat(range.end_week)
                  : range.end_week
              ),
          },
        ];

        // Place region filter first or last depending on parameter
        return includeRegionFirst
          ? [regionIdFilter, ...dateFilters]
          : [...dateFilters, regionIdFilter];
      };

      // Safely destructure dateRanges with defaults
      const {
        overallPerformanceRange,
        compareOverallPerformanceRangeTableOne,
        compareOverallPerformanceRangeYTD,
        overallPerformanceYTDRange,
      } = dateRanges || {};

      // Batch state updates to reduce renders
      const updates = [];

      const newCurrentDateFilters = createRegionDateIndexFilter(overallPerformanceRange, true);
      if (
        newCurrentDateFilters &&
        JSON.stringify(newCurrentDateFilters) !== JSON.stringify(currentDateFiltersForRegion)
      ) {
        updates.push(() => setCurrentDateFiltersForRegion(newCurrentDateFilters));
      }

      const newComparePeriodFilters = createRegionDateIndexFilter(
        compareOverallPerformanceRangeTableOne,
        true
      );
      if (
        newComparePeriodFilters &&
        JSON.stringify(newComparePeriodFilters) !== JSON.stringify(comparePeriodFiltersForRegion)
      ) {
        updates.push(() => setComparePeriodFiltersForRegion(newComparePeriodFilters));
      }

      const newCompareYtdFilters = createRegionDateIndexFilter(
        compareOverallPerformanceRangeYTD,
        false
      );
      if (
        newCompareYtdFilters &&
        JSON.stringify(newCompareYtdFilters) !== JSON.stringify(compareYtdFiltersForRegion)
      ) {
        updates.push(() => setCompareYtdFiltersForRegion(newCompareYtdFilters));
      }

      const newCurrentYtdFilters = createRegionDateIndexFilter(overallPerformanceYTDRange, false);
      if (
        newCurrentYtdFilters &&
        JSON.stringify(newCurrentYtdFilters) !== JSON.stringify(currentYtdFiltersForRegion)
      ) {
        updates.push(() => setCurrentYtdFiltersForRegion(newCurrentYtdFilters));
      }

      // Execute all updates
      updates.forEach((update) => update());
    } catch (error) {
      console.error('Error in useRegionFilters:', error);
    }
  }, [
    regionId,
    regionArray,
    dateRanges,
    showSelectedRegion,
    defaultRegionBoolean,
    checkForGlobalObject,
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
    compareYtdFiltersForRegion,
    currentYtdFiltersForRegion,
  ]);

  return {
    currentDateFiltersForRegion,
    comparePeriodFiltersForRegion,
    compareYtdFiltersForRegion,
    currentYtdFiltersForRegion,
  };
}
